import { FloorPolylineSegmentGeometry, Point2D, TrussEnvelope } from '@paragon/contract-library';

import { ITransformation } from '../../library/transformations/types';
import type { RootState } from '../../store';
import { deleteItems } from '../../store/actions/deletionActions';
import {
  finishInsertingFeaturePatternContainer,
  startInsertingFeaturePatternContainer,
} from '../../store/actions/layout/featurePatternContainerActions';
import { optimizeFeaturePatternContainerLayout } from '../../store/actions/layout/layoutActions';
import { applyExplicitTransformation } from '../../store/actions/layout/transformationActions';
import { insertCornerSet } from '../../store/actions/layout/trussEnvelopeActions';
import {
  finishWorkLineInsertionFromGeometries,
  startInsertWorkLine,
  toggleWorkLineBoundingStatus,
} from '../../store/actions/layout/workLineActions';
import { selectItemsByGuid } from '../../store/actions/selectActions';
import {
  clearInsertingObject,
  CornerSetType,
  updateInsertionFeaturePatternContainer,
} from '../../store/slices/applicationSlice';
import { describeFloorArea, describeRoofPlane } from './contextTools';
import { requireProject, requireString, waitForAppToSettle } from './helpers';
import { defineTool, ToolError } from './types';

const POINT_SCHEMA = {
  type: 'object',
  properties: { x: { type: 'number' }, y: { type: 'number' } },
  required: ['x', 'y'],
  description: 'Inches in the level’s plan coordinates.',
};

function requirePoint(value: unknown, label: string): Point2D {
  const point = value as Partial<Point2D> | null;
  if (typeof point?.x !== 'number' || typeof point?.y !== 'number')
    throw new ToolError(`"${label}" must be a point with numeric x and y, in inches.`);
  return { x: point.x, y: point.y };
}

/** The kinds of layout objects the Assistant can move, copy, rotate, or mirror, by the app’s selection types. */
export const TRANSFORMABLE_KINDS = {
  wall: 'wall',
  beam: 'beam',
  trussEnvelope: 'trussEnvelope',
  roofPlane: 'roofPlane',
  workLine: 'workLine',
  floorArea: 'featurePatternContainer',
} as const;

type TransformableKind = keyof typeof TRANSFORMABLE_KINDS;

function sameName(a: string, b: string) {
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

/**
 * Finds layout objects of one kind by the names the Assistant knows them by (roof planes by the ids get_layout gives).
 */
export function findLayoutObjectGuids(state: RootState, kind: TransformableKind, names: readonly string[]): string[] {
  const { layout } = state;
  return names.map((name) => {
    const matches = (() => {
      switch (kind) {
        case 'wall':
        case 'beam':
          return Object.values(layout.bearingEnvelopes)
            .filter((envelope) => (envelope.bearingType === 'Beam') === (kind === 'beam'))
            .filter((envelope) => sameName(envelope.name, name));
        case 'trussEnvelope':
          return Object.values(layout.trussEnvelopes).filter((envelope) => sameName(envelope.name, name));
        case 'roofPlane':
          return Object.values(layout.roofPlanes).filter((plane) => plane.guid.startsWith(name.trim()));
        case 'workLine':
          return Object.values(layout.workLines).filter((line) => sameName(line.name, name));
        case 'floorArea':
          return Object.values(layout.featurePatternContainers).filter((container) => sameName(container.name, name));
      }
    })();
    if (matches.length === 0) throw new ToolError(`There is no ${kind} named "${name}".`);
    if (matches.length > 1) throw new ToolError(`More than one ${kind} is named "${name}".`);
    return matches[0].guid;
  });
}

interface TransformInput {
  readonly kind: TransformableKind;
  readonly names: string[];
  readonly operation: 'move' | 'rotate' | 'mirror';
  readonly copy?: boolean;
  readonly by?: { x: number; y: number; z?: number };
  readonly center?: unknown;
  readonly angle?: number;
  readonly mirrorFrom?: unknown;
  readonly mirrorTo?: unknown;
}

/**
 * Builds the transformation the app's Move, Rotate, and Mirror commands build from the designer's clicks.
 */
export function buildTransformation(input: TransformInput): ITransformation {
  const base = { duplicate: input.copy ?? false, reassign: false as const, preserveLinkedComponents: true };
  switch (input.operation) {
    case 'move': {
      const by = input.by;
      if (typeof by?.x !== 'number' || typeof by?.y !== 'number')
        throw new ToolError('"by" must give x and y offsets in inches.');
      return {
        ...base,
        type: 'movement',
        origin: { x: 0, y: 0, z: 0 },
        destination: { x: by.x, y: by.y, z: by.z ?? 0 },
      };
    }
    case 'rotate': {
      if (typeof input.angle !== 'number' || !Number.isFinite(input.angle))
        throw new ToolError('"angle" must be a number of degrees, counterclockwise.');
      return { ...base, type: 'rotation', center: requirePoint(input.center, 'center'), angle: input.angle };
    }
    case 'mirror': {
      const basePoint = requirePoint(input.mirrorFrom, 'mirrorFrom');
      const endPoint = requirePoint(input.mirrorTo, 'mirrorTo');
      if (basePoint.x === endPoint.x && basePoint.y === endPoint.y)
        throw new ToolError('The mirror line needs two different points.');
      return {
        ...base,
        type: 'mirror',
        center: { type: 'work-line', originObjectGuid: null, definition: { basePoint, endPoint } },
      };
    }
    default:
      throw new ToolError('"operation" must be move, rotate, or mirror.');
  }
}

/**
 * Builds a floor area's boundary from the walls it sits against, in order around it, or from its corners.
 */
export function buildFloorBoundary(
  state: RootState,
  input: { walls?: string[]; corners?: unknown[] },
): FloorPolylineSegmentGeometry[] {
  if (input.walls != null && input.walls.length > 0) {
    if (input.walls.length < 3) throw new ToolError('A floor area needs at least three walls around it.');
    return findLayoutObjectGuids(state, 'wall', input.walls).map((bearingEnvelopeGuid) => ({
      type: 'AgainstBearing',
      bearingEnvelopeGuid,
      flipped: false,
    }));
  }

  const corners = (input.corners ?? []).map((corner, index) => requirePoint(corner, `corners[${index}]`));
  if (corners.length < 3) throw new ToolError('Give the walls around the floor area, or at least three corners.');
  return corners.map((corner, index) => ({
    type: 'AbsoluteVertical',
    segment: { basePoint: corner, endPoint: corners[(index + 1) % corners.length] },
    isInfinite: false,
  }));
}

const CENTER_JACK_TOLERANCE = 3;

/** Envelopes square to a girder with an end on it: the center jack a corner set puts at a hip end. */
export function findCenterJacksOnGirder(state: RootState, girder: TrussEnvelope) {
  const along = { x: girder.rightPoint.x - girder.leftPoint.x, y: girder.rightPoint.y - girder.leftPoint.y };
  const length = Math.hypot(along.x, along.y);
  if (length === 0) return [];
  const unit = { x: along.x / length, y: along.y / length };
  const distanceFromGirder = (point: Point2D) => {
    const t = (point.x - girder.leftPoint.x) * unit.x + (point.y - girder.leftPoint.y) * unit.y;
    if (t < -CENTER_JACK_TOLERANCE || t > length + CENTER_JACK_TOLERANCE) return Infinity;
    return Math.abs((point.x - girder.leftPoint.x) * unit.y - (point.y - girder.leftPoint.y) * unit.x);
  };
  return Object.values(state.layout.trussEnvelopes).filter((envelope) => {
    if (envelope.guid === girder.guid || envelope.levelGuid !== girder.levelGuid) return false;
    const dx = envelope.rightPoint.x - envelope.leftPoint.x;
    const dy = envelope.rightPoint.y - envelope.leftPoint.y;
    const envelopeLength = Math.hypot(dx, dy);
    if (envelopeLength === 0) return false;
    const square = Math.abs((dx * unit.x + dy * unit.y) / envelopeLength) < 0.02;
    return (
      square &&
      Math.min(distanceFromGirder(envelope.leftPoint), distanceFromGirder(envelope.rightPoint)) <= CENTER_JACK_TOLERANCE
    );
  });
}

export const layoutStructureTools = [
  defineTool<TransformInput>({
    name: 'transform_layout',
    description:
      'Moves, rotates, or mirrors layout objects of one kind, as Move, Rotate, and Mirror do, optionally leaving the ' +
      'originals in place (copy). Moves are offsets in inches; rotations are degrees counterclockwise about a plan ' +
      'point; mirrors are about the line through two plan points. Copies of walls and envelopes get the next names, ' +
      'and copied envelopes keep their trusses.',
    inputSchema: {
      type: 'object',
      properties: {
        kind: { type: 'string', enum: Object.keys(TRANSFORMABLE_KINDS) },
        names: { type: 'array', items: { type: 'string' }, description: 'Roof planes by the ids get_layout gives.' },
        operation: { type: 'string', enum: ['move', 'rotate', 'mirror'] },
        copy: { type: 'boolean', description: 'Keep the originals and transform copies. Defaults to false.' },
        by: {
          type: 'object',
          properties: { x: { type: 'number' }, y: { type: 'number' }, z: { type: 'number' } },
          description: 'For move: the offset in inches; z raises it.',
        },
        center: POINT_SCHEMA,
        angle: { type: 'number', description: 'For rotate: degrees, counterclockwise.' },
        mirrorFrom: POINT_SCHEMA,
        mirrorTo: POINT_SCHEMA,
      },
      required: ['kind', 'names', 'operation'],
    },
    changesProject: true,
    async execute(input, { dispatch, getState }) {
      requireProject(getState());
      if (!(input.kind in TRANSFORMABLE_KINDS))
        throw new ToolError(`"kind" must be one of ${Object.keys(TRANSFORMABLE_KINDS).join(', ')}.`);
      if (!Array.isArray(input.names) || input.names.length === 0) throw new ToolError('Name what to transform.');

      const guids = findLayoutObjectGuids(getState(), input.kind, input.names);
      const transformation = buildTransformation(input);
      const before = summarizeKind(getState(), input.kind);

      // The app transforms the selection, as when the designer selects objects and then picks Move.
      await dispatch(selectItemsByGuid(guids, TRANSFORMABLE_KINDS[input.kind], { force: true }));
      await dispatch(applyExplicitTransformation(transformation));
      await waitForAppToSettle(getState);

      const after = summarizeKind(getState(), input.kind);
      const added = after.filter((item) => !before.some((other) => other.guid === item.guid));
      return {
        summary: {
          [input.copy ? 'copies' : 'transformed']: (input.copy
            ? added
            : after.filter(({ guid }) => guids.includes(guid))
          ).map(({ guid: _guid, ...item }) => item),
        },
      };
    },
  }),

  defineTool<{
    girder: string;
    planes: [string, string];
    style?: CornerSetType;
    spacing?: number;
    minJackLength?: number;
  }>({
    name: 'add_hip_corner',
    description:
      'Fills a hip corner with jacks, as Create Corner Set does: give the hip girder’s truss envelope and the ids of ' +
      'the two roof planes that meet at the corner. Style is Stepdown (default) or Atlantic; spacing and minimum ' +
      'jack length are in inches (defaults 24 and 3). A hip end has two corners on one girder: add both, one call ' +
      'each. Each corner set makes the center jack, so the second call replaces the first one’s, which lets its hip ' +
      'reach the girder too. Design the new envelopes with design_trusses, then analyze the jacks before the girder.',
    inputSchema: {
      type: 'object',
      properties: {
        girder: { type: 'string', description: 'The hip girder’s truss envelope name.' },
        planes: { type: 'array', items: { type: 'string' }, description: 'Two roof plane ids from get_layout.' },
        style: { type: 'string', enum: ['Stepdown', 'Atlantic'] },
        spacing: { type: 'number' },
        minJackLength: { type: 'number' },
      },
      required: ['girder', 'planes'],
    },
    changesProject: true,
    async execute(input, { dispatch, getState }) {
      requireProject(getState());
      if (!Array.isArray(input.planes) || input.planes.length !== 2)
        throw new ToolError('Give exactly two roof planes.');
      const [girderGuid] = findLayoutObjectGuids(getState(), 'trussEnvelope', [requireString(input, 'girder')]);
      const [plane1Guid, plane2Guid] = findLayoutObjectGuids(getState(), 'roofPlane', input.planes);
      const spacing = input.spacing ?? 24;
      const minJackLength = input.minJackLength ?? 3;
      if (spacing <= 0 || minJackLength < 0) throw new ToolError('Spacing must be more than 0.');

      // A center jack an earlier corner set put on this girder: square to the girder, with an end on it. This set
      // makes it again, and its hip would stop against the old one instead of reaching the girder.
      const girder = getState().layout.trussEnvelopes[girderGuid];
      const replaced = girder == null ? [] : findCenterJacksOnGirder(getState(), girder);
      if (replaced.length > 0) {
        await dispatch(deleteItems(replaced.map((envelope) => ({ type: 'trussEnvelope' as const, item: envelope }))));
        await waitForAppToSettle(getState);
      }

      const envelopesBefore = new Set(Object.keys(getState().layout.trussEnvelopes));
      const errorsBefore = getState().notifications;
      await dispatch(
        insertCornerSet(input.style ?? 'Stepdown', plane1Guid, plane2Guid, girderGuid, spacing, minJackLength),
      );
      await waitForAppToSettle(getState);

      const added = Object.values(getState().layout.trussEnvelopes).filter(
        (envelope) => !envelopesBefore.has(envelope.guid),
      );
      if (added.length === 0)
        throw new ToolError(
          getState().notifications !== errorsBefore
            ? 'The app could not make a corner set there; check that the planes meet at a hip and the girder crosses its ridge.'
            : 'The app made no jacks for that corner.',
        );
      return {
        summary: {
          added: added.map(({ name, leftPoint, rightPoint }) => ({ name, from: leftPoint, to: rightPoint })),
          ...(replaced.length > 0
            ? { replacedCenterJack: replaced.map(({ name }) => name), note: 'The new set’s center jack replaces it.' }
            : {}),
        },
      };
    },
  }),

  defineTool<{ lines: { from: unknown; to: unknown }[]; bounding?: boolean }>({
    name: 'add_work_lines',
    description:
      'Adds work lines, the construction lines envelopes and trims snap to, along segments in plan inches. Bounding ' +
      'work lines also cut truss envelopes that cross them. Returns their names (WL-1 and so on).',
    inputSchema: {
      type: 'object',
      properties: {
        lines: {
          type: 'array',
          items: { type: 'object', properties: { from: POINT_SCHEMA, to: POINT_SCHEMA }, required: ['from', 'to'] },
        },
        bounding: { type: 'boolean', description: 'Defaults to the app’s work line setting.' },
      },
      required: ['lines'],
    },
    changesProject: true,
    async execute(input, { dispatch, getState }) {
      requireProject(getState());
      if (!Array.isArray(input.lines) || input.lines.length === 0) throw new ToolError('Give at least one line.');
      const segments = input.lines.map((line, index) => {
        const basePoint = requirePoint(line.from, `lines[${index}].from`);
        const endPoint = requirePoint(line.to, `lines[${index}].to`);
        if (basePoint.x === endPoint.x && basePoint.y === endPoint.y)
          throw new ToolError(`lines[${index}] starts and ends at the same point.`);
        return { basePoint, endPoint };
      });

      const linesBefore = new Set(Object.keys(getState().layout.workLines));
      dispatch(startInsertWorkLine());
      try {
        await dispatch(finishWorkLineInsertionFromGeometries(segments));
      } finally {
        dispatch(clearInsertingObject());
      }

      const added = Object.values(getState().layout.workLines).filter((line) => !linesBefore.has(line.guid));
      if (added.length === 0) throw new ToolError('The app could not add the work lines.');
      if (input.bounding != null) {
        const toToggle = added.filter((line) => line.bounding !== input.bounding).map(({ guid }) => guid);
        if (toToggle.length > 0) await dispatch(toggleWorkLineBoundingStatus(toToggle));
      }
      await waitForAppToSettle(getState);

      return {
        summary: {
          added: added.map(({ guid }) => {
            const line = getState().layout.workLines[guid];
            return { name: line.name, bounding: line.bounding, geometry: line.geometry };
          }),
        },
      };
    },
  }),

  defineTool<{ walls?: string[]; corners?: unknown[]; bottomElevation?: number; topElevation?: number; name?: string }>(
    {
      name: 'add_floor_area',
      description:
        'Adds a floor area, the container floor trusses are laid out in, as Create > Feature Pattern Container does. ' +
        'Bound it by the walls around it, in order, or by its corners in plan inches. Elevations (inches) default to ' +
        'the active level’s. Lay it out with layout_floor.',
      inputSchema: {
        type: 'object',
        properties: {
          walls: { type: 'array', items: { type: 'string' }, description: 'Wall names, in order around the area.' },
          corners: { type: 'array', items: POINT_SCHEMA },
          bottomElevation: { type: 'number' },
          topElevation: { type: 'number' },
          name: { type: 'string' },
        },
      },
      changesProject: true,
      async execute(input, { dispatch, getState }) {
        requireProject(getState());
        const geometry = buildFloorBoundary(getState(), input);
        const containersBefore = new Set(Object.keys(getState().layout.featurePatternContainers));

        await dispatch(startInsertingFeaturePatternContainer());
        try {
          const insertion = getState().application.insertingObjectInfo;
          if (insertion?.type !== 'featurePatternContainer') throw new ToolError('Could not start a floor area.');
          dispatch(
            updateInsertionFeaturePatternContainer({
              ...(input.name != null ? { name: input.name } : {}),
              shape: {
                geometry,
                bottomElevation: input.bottomElevation ?? insertion.data.shape.bottomElevation,
                topElevation: input.topElevation ?? insertion.data.shape.topElevation,
              },
            }),
          );
          await dispatch(finishInsertingFeaturePatternContainer());
        } finally {
          dispatch(clearInsertingObject());
        }
        await waitForAppToSettle(getState);

        const added = Object.values(getState().layout.featurePatternContainers).find(
          (container) => !containersBefore.has(container.guid),
        );
        if (added == null) throw new ToolError('The app could not add the floor area.');
        return {
          summary: { added: describeFloorArea(getState(), added) },
        };
      },
    },
  ),

  defineTool<{ area: string }>({
    name: 'layout_floor',
    description:
      'Fills a floor area with floor truss envelopes, as Place Optimized Truss Envelopes does. The area must have no ' +
      'envelopes yet. Generate the trusses afterward with design_trusses.',
    inputSchema: {
      type: 'object',
      properties: { area: { type: 'string', description: 'The floor area’s name.' } },
      required: ['area'],
    },
    changesProject: true,
    async execute(input, { dispatch, getState }) {
      requireProject(getState());
      const [containerGuid] = findLayoutObjectGuids(getState(), 'floorArea', [requireString(input, 'area')]);
      const inArea = () =>
        Object.values(getState().layout.trussEnvelopes).filter(
          (envelope) => envelope.featurePatternContainerGuid === containerGuid,
        );
      if (inArea().length > 0) throw new ToolError('That floor area already has truss envelopes.');

      await dispatch(optimizeFeaturePatternContainerLayout(containerGuid));
      await waitForAppToSettle(getState);

      const envelopes = inArea();
      if (envelopes.length === 0) throw new ToolError('The app placed no envelopes in that floor area.');
      return {
        summary: {
          trussEnvelopes: envelopes.map(({ name, leftPoint, rightPoint, numberOfPlies }) => ({
            name,
            from: leftPoint,
            to: rightPoint,
            plies: numberOfPlies,
          })),
        },
      };
    },
  }),
];

function summarizeKind(state: RootState, kind: TransformableKind) {
  const { layout } = state;
  switch (kind) {
    case 'wall':
    case 'beam':
      return Object.values(layout.bearingEnvelopes).map(({ guid, name, leftPoint, rightPoint }) => ({
        guid,
        name,
        from: leftPoint,
        to: rightPoint,
      }));
    case 'trussEnvelope':
      return Object.values(layout.trussEnvelopes).map(({ guid, name, leftPoint, rightPoint }) => ({
        guid,
        name,
        from: leftPoint,
        to: rightPoint,
      }));
    case 'roofPlane':
      return Object.values(layout.roofPlanes).map((plane) => ({
        guid: plane.guid,
        ...describeRoofPlane(state, plane),
      }));
    case 'workLine':
      return Object.values(layout.workLines).map(({ guid, name, geometry }) => ({ guid, name, geometry }));
    case 'floorArea':
      return Object.values(layout.featurePatternContainers).map((container) => ({
        guid: container.guid,
        ...describeFloorArea(state, container),
      }));
  }
}
